import { $fetchAllo } from '@/services/api-allo/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type ContactAttributeConfigItem = {
  column_key: string;
  attribute_id?: number | null;
  display_order: number;
  is_visible: boolean;
};

type ContactAttributeConfigResponse = {
  data: {
    columns: ContactAttributeConfigItem[];
  };
};

export const CONTACT_ATTRIBUTE_CONFIG_QUERY_KEY = ['contact-attribute-config'] as const;

export function useContactAttributeConfig() {
  return useQuery({
    queryKey: CONTACT_ATTRIBUTE_CONFIG_QUERY_KEY,
    queryFn: async () => {
      const res = await $fetchAllo.GET('/v1/contact/attribute-config' as any, {});
      return res.data as ContactAttributeConfigResponse | undefined;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateContactAttributeConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      columns: { column_key: string; display_order: number; is_visible: boolean }[],
    ) => {
      return $fetchAllo.PUT('/v1/contact/attribute-config' as any, {
        body: { columns },
      });
    },
    onMutate: async (columns) => {
      await queryClient.cancelQueries({ queryKey: CONTACT_ATTRIBUTE_CONFIG_QUERY_KEY });
      const previousData = queryClient.getQueryData(CONTACT_ATTRIBUTE_CONFIG_QUERY_KEY);

      queryClient.setQueryData(CONTACT_ATTRIBUTE_CONFIG_QUERY_KEY, {
        data: {
          columns: columns.map((c) => ({
            column_key: c.column_key,
            display_order: c.display_order,
            is_visible: c.is_visible,
          })),
        },
      });

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(CONTACT_ATTRIBUTE_CONFIG_QUERY_KEY, context.previousData);
      }
    },
  });
}
